import React from 'react';
import lodashCompact from 'lodash/fp/compact';

import {
  ID_VERIFICATION_FLOW_RENTAL_APPLICATION,
  ID_VERIFICATION_METHOD_MANUAL,
} from 'common/idVerificationConstants';

import {
  convertStateCodeToStateName,
} from 'common/util/stateCodes';

import ButtonDeprecated, {
  BUTTON_THEME_BLUE_GREYCLIFF,
} from 'client/components/ButtonDeprecated';
import SrButton, {
  SR_BUTTON_HEIGHT_TALL,
  SR_BUTTON_THEME_ADOBE_BLUE_BORDER,
} from 'client/components/SrButton/SrButton';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrIcon from 'client/components/SrIcon';
import SrIdVerification from 'client/components/SrIdVerification';

import './SrRentalApplicationIdVerification.less';

const SrRentalApplicationIdVerification = ({
  listing,
  currentUser,
  renterProfile,
  onComplete,
}) => {
  return (
    <SrIdVerification
      listingId={listing.id}
      userId={currentUser.id}
      idVerificationFlow={ID_VERIFICATION_FLOW_RENTAL_APPLICATION}
      onComplete={onComplete}
    >
      {({ idVerification, inProgress, hasError, onStartVerification }) => {
        let contentDOM = null;

        if (idVerification) {
          const { vouchedJob } = idVerification;
          const isPassport = vouchedJob.result.type === 'passport';

          const vouchedItems = lodashCompact([
            {
              title: isPassport ? '#' : 'Government License #',
              value: vouchedJob.result.id,
            },
            {
              title: 'D.O.B',
              value: vouchedJob.result.dob,
            },
            isPassport && {
              title: 'Country',
              value: vouchedJob.result.country,
            },
            !isPassport && vouchedJob.result.state && {
              title: 'State',
              value: convertStateCodeToStateName(vouchedJob.result.state),
            },
            {
              title: 'Expires',
              value: vouchedJob.result.expireDate,
            },
          ]);

          contentDOM = (
            <div className='SrFormFieldGroupContent'>
              <div styleName='governmentIdContainer info'>
                <span styleName='resultText'>
                  {vouchedJob.result.firstName + ' ' + vouchedJob.result.lastName}
                </span>
                <div styleName='line' />
                <SrFlex wrap='wrap'>
                  {vouchedItems.map(({ title, value }) => (
                    <SrFlex direction='col' key={title} styleName='resultValue'>
                      <span styleName='title'>{title}</span>
                      <span styleName='value'>{value}</span>
                    </SrFlex>
                  ))}
                </SrFlex>
                <SrButton
                  height='60px'
                  onClick={onStartVerification}
                  heightType={SR_BUTTON_HEIGHT_TALL}
                  theme={SR_BUTTON_THEME_ADOBE_BLUE_BORDER}
                >
                  Edit
                </SrButton>
                <SrFlex justify='center' marginTop='16px'>
                  <img alt='Private, secure, and encrypted' src='/images/lock-private-secure-encrypted.svg' />
                </SrFlex>
              </div>
            </div>
          );
        }
        else if (inProgress) {
          contentDOM = (
            <div className='SrFormFieldGroupContent'>
              <div styleName='governmentIdContainer'>
                <SrFlex direction='col' align='center'>
                  <SrIcon className='id-verification-timer' />
                  <SrText weight='500' padding='4px 0 8px'>
                    Please wait, we’re verifying your ID...
                  </SrText>
                  <SrText weight='500' size='14'>
                    This usually takes around 20 seconds
                  </SrText>
                </SrFlex>
              </div>
            </div>
          );
        }
        else if (hasError) {
          contentDOM = (
            <div className='SrFormFieldGroupContent'>
              <div styleName='governmentIdContainer error'>
                <SrText color='orange300'>
                  Oops! It looks like something went wrong and we couldn’t verify your ID.
                  Try again using the button below! If you’ve already tried multiple times,
                  please contact the Sunroom Rentals support number that has been texting you.
                </SrText>
                <SrFlex marginTop='16px'>
                  <ButtonDeprecated
                    height='60px'
                    onClick={onStartVerification}
                    theme={BUTTON_THEME_BLUE_GREYCLIFF}
                  >
                    Verify ID
                  </ButtonDeprecated>
                </SrFlex>
              </div>
            </div>
          );
        }
        else if (renterProfile.idVerificationMethod === ID_VERIFICATION_METHOD_MANUAL) {
          contentDOM = (
            <div className='SrFormFieldGroupContent'>
              <div styleName='governmentIdContainer'>
                <SrFlex direction='col' align='center'>
                  <SrIcon className='id-verification-ok' />
                  <SrFlex height='16px' />
                  <span styleName='resultText'>ID Manually Verified by Sunroom</span>
                </SrFlex>
              </div>
            </div>
          );
        }
        else {
          contentDOM = (
            <div className='SrFormFieldGroupContent'>
              <div styleName='governmentIdContainer'>
                <SrFlex marginBottom='29px' align='center'>
                  <div styleName='gonvernmentIdIcon' />
                  <span styleName='governmentIdText'>
                    Government ID required for identity verification
                  </span>
                </SrFlex>
                <div>
                  <ButtonDeprecated
                    height='60px'
                    onClick={onStartVerification}
                    theme={BUTTON_THEME_BLUE_GREYCLIFF}
                  >
                    Verify ID
                  </ButtonDeprecated>
                </div>
                <SrFlex justify='center' marginTop='16px'>
                  <img alt='Private, secure, and encrypted' src='/images/lock-private-secure-encrypted.svg' />
                </SrFlex>
              </div>
            </div>
          );
        }

        return (
          <div className='SrFormFieldGroup' id='field-idVerificationMethod'>
            <div className='SrFormFieldGroupTitle'>
              identification
            </div>
            {contentDOM}
          </div>
        );
      }}
    </SrIdVerification>
  );
};

SrRentalApplicationIdVerification.displayName = 'SrRentalApplicationIdVerification';

export default SrRentalApplicationIdVerification;
